import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminApi, AdminEmailRow } from '@/lib/admin';
import { AdminShell } from '@/components/app/AdminShell';

const STATUS_TONES: Record<AdminEmailRow['status'], string> = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-warn-500/10 text-warn-600 border-warn-500/30',
  skipped: 'bg-ink-100 text-ink-600 border-ink-200',
};

const KIND_TONES: Record<AdminEmailRow['kind'], string> = {
  verify: 'bg-accent-50 text-accent-700 border-accent-200',
  reset: 'bg-citation-50 text-citation-700 border-citation-200',
  welcome: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  admin_broadcast: 'bg-warn-500/10 text-warn-700 border-warn-500/30',
  other: 'bg-ink-100 text-ink-700 border-ink-200',
};

export function AdminEmailsPage() {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['admin', 'emails'],
    queryFn: () => adminApi.recentEmails(200),
    refetchInterval: 30_000,
  });

  return (
    <AdminShell
      title={t('admin.emails.title', { defaultValue: 'Email audit trail' })}
      description={t('admin.emails.subtitle', {
        defaultValue: 'Every outbound transactional email — sends, skips, failures.',
      }) as string}
    >
      {isLoading ? (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="card-paper p-12 text-center text-ink-500 text-sm">
          {t('admin.emails.empty', { defaultValue: 'No emails have been sent yet.' })}
        </div>
      ) : (
        <div className="card-paper overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100 text-[11px] uppercase tracking-[0.12em]">
                <th className="py-2.5 pl-5 pr-3 font-medium">{t('admin.emails.th_when')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.emails.th_kind')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.emails.th_to')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.emails.th_subject')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.emails.th_status')}</th>
                <th className="py-2.5 px-3 pr-5 font-medium">{t('admin.emails.th_note')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-ink-50 align-top hover:bg-paper-100/40">
                  <td className="py-2.5 pl-5 pr-3 num text-ink-700 text-xs whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`rounded-md text-[10.5px] px-1.5 py-0.5 border font-semibold uppercase tracking-[0.1em] ${KIND_TONES[e.kind]}`}
                    >
                      {e.kind}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 num text-[12px] text-ink-700">{e.to_email}</td>
                  <td className="py-2.5 px-3 text-[12.5px] text-ink-900 max-w-[28ch] truncate" title={e.subject}>
                    {e.subject}
                  </td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`rounded-md text-[10.5px] px-1.5 py-0.5 border font-semibold uppercase tracking-[0.1em] ${STATUS_TONES[e.status]}`}
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 pr-5 text-[11.5px] text-ink-500 max-w-[28ch] truncate" title={e.error_text ?? ''}>
                    {e.error_text ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
